import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import * as Dialog from '@/components/ui/dialog';
import { useAuth } from '@/auth/use-auth';
import { refreshCache } from '@/lib/sales-api';
import { fmtDate } from '@/lib/format';

interface Props {
  /** Visible date window — refresh runs against [from, to] inclusive. */
  from: string;
  to: string;
  store: string | null;
  /** Which cache(s) to invalidate. Defaults to whichever report this is. */
  which: 'salesman' | 'estimate' | 'all';
  /** Query keys to invalidate after a successful refresh. */
  invalidateKeys: ReadonlyArray<unknown>;
  /** Optional callout text — defaults to "Refresh cache". */
  label?: string;
}

/**
 * Manual force-refresh trigger for the per-day report MV cache. Visible to
 * roles with `sales:refresh-cache` permission (admin / owner / manager).
 *
 * After server-side refresh completes, busts the relevant React Query
 * caches so the page re-fetches and renders the freshly-synced rows.
 */
export function RefreshCacheButton({ from, to, store, which, invalidateKeys, label }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [status, setStatus] = React.useState<string | null>(null);

  const canRefresh = !!user && ['admin', 'owner', 'manager'].includes(user.role);
  if (!canRefresh) return null;

  const dayCount = countDays(from, to);
  const whichLabel = which === 'all' ? 'salesman + estimate' : which;

  const runRefresh = async () => {
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const r = await refreshCache({ from, to, store, which });
      const rowSum = r.results
        .filter((x) => which === 'all' || x.kind === which)
        .reduce((s, x) => s + Math.max(0, x.rowsWritten), 0);
      setStatus(`✓ ${r.daysRefreshed}d · ${rowSum} rows · ${(r.totalDurationMs / 1000).toFixed(1)}s`);
      for (const k of invalidateKeys) {
        qc.invalidateQueries({ queryKey: k as any });
      }
      setOpen(false);
    } catch (e) {
      setStatus(`✗ ${(e as Error).message.slice(0, 80)}`);
    } finally {
      setBusy(false);
      setTimeout(() => setStatus(null), 5000);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Dialog.Root open={open} onOpenChange={(v) => { if (!busy) setOpen(v); }}>
        <Dialog.Trigger asChild>
          <Button
            variant="outline"
            size="sm"
            title={`Re-sync ${whichLabel} cache for ${from}..${to}`}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            {label ?? 'Refresh cache'}
          </Button>
        </Dialog.Trigger>
        <Dialog.Content className="sm:max-w-md">
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-amber-100 p-2 text-amber-700">
              <AlertCircle className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <Dialog.Title>Re-pull data from MSSQL?</Dialog.Title>
              <p className="mt-1.5 text-sm text-muted-foreground">
                This will rebuild the cached <span className="font-medium text-foreground">{whichLabel}</span>{' '}
                report data for{' '}
                <span className="font-medium text-foreground">{fmtDate(from)} → {fmtDate(to)}</span>{' '}
                ({dayCount} day{dayCount === 1 ? '' : 's'})
                {store ? <> on store <span className="font-mono">{store}</span></> : null}.
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Use this after a backdated bill is edited in the POS. A 30-day refresh
                typically takes ~30s; the page will reload automatically when done.
              </p>
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button size="sm" disabled={busy} onClick={runRefresh}>
              <RefreshCw className={`mr-2 h-4 w-4 ${busy ? 'animate-spin' : ''}`} />
              {busy ? 'Refreshing…' : 'Yes, refresh'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Root>
      {status && <span className="text-xs text-muted-foreground">{status}</span>}
    </div>
  );
}

function countDays(from: string, to: string): number {
  // ISO yyyy-mm-dd, inclusive day count. Uses local date math via Date()
  // so DST/timezone shifts don't slip in (we never look at hours).
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  const a = new Date(fy!, (fm! - 1), fd!);
  const b = new Date(ty!, (tm! - 1), td!);
  const ms = b.getTime() - a.getTime();
  return Math.max(1, Math.round(ms / 86_400_000) + 1);
}
